import { ApiError, UnauthenticatedError } from '@lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { createComponent, listComponents, type CreateComponentInput } from '../api/components';

/**
 * Server state for the component catalog.
 *
 * Its own key space rather than a branch of `flagKeys`, even though a component
 * is made of flags: the rows come from a different partition, a different route
 * and a different tier, and nothing in one can be derived from the other. What
 * a component *contains* is a list of keys, not a list of flags.
 *
 * Nothing here is optimistic, for the reason `use-flags.ts` sets out at length:
 * this is a control plane, and an unconfirmed value is worse than a brief
 * pending state. A creation is also the least optimistic write there is — the
 * id is permanent, and the two ways it fails (a taken id, a member that is not
 * an entitlement) are both things the server knows and the client does not.
 */

export const componentKeys = {
    all: ['components'] as const,
    list: () => [...componentKeys.all, 'list'] as const,
};

/**
 * The same retry policy the flags queries use, and deliberately the same
 * function shape: a 4xx is an answer rather than a blip, an expired session is
 * an answer too, and only a transport failure is worth one retry.
 */
function retryUnlessClientError(failureCount: number, error: Error): boolean {
    if (error instanceof UnauthenticatedError) return false;
    if (error instanceof ApiError && error.status < 500) return false;
    return failureCount < 1;
}

export function useComponents() {
    return useQuery({
        queryKey: componentKeys.list(),
        queryFn: ({ signal }) => listComponents(signal),
        retry: retryUnlessClientError,
        // Load-bearing, as on every other query here: under the default
        // `networkMode: 'online'` a failure React Query reads as a connectivity
        // problem parks the query in `paused` rather than surfacing an error,
        // and a screen rendering a spinner on `isPending` would spin forever.
        networkMode: 'always',
    });
}

/**
 * Create a component, then refetch the list.
 *
 * `useFlagMutation`'s posture, minus the parts that have nothing to answer to
 * yet. The list is invalidated and the invalidation is **returned**, so the
 * mutation stays `isPending` until the refetch lands — otherwise the dialog
 * closes onto a table that does not yet show the row that was just created, and
 * the tab count above it disagrees with the rows below.
 *
 * There is no detail query to invalidate and no version-conflict branch: this
 * slice has one read, and creation is the only write. When editing a component
 * arrives it brings a version guard with it, and this should become the shared
 * mutation helper rather than grow a second copy of that reasoning.
 *
 * Never retried, for the reason flag writes are not: a retried create files a
 * second audit entry, and the second attempt would fail on the id the first one
 * took anyway.
 */
export function useCreateComponent() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: CreateComponentInput) => createComponent(input),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: componentKeys.list() }),
        retry: false,
        networkMode: 'always',
    });
}
